import React, { useState, useEffect, useRef } from "react";
import { Box, Button, Menu, MenuItem, Separator } from "@showitinc/showit-wc";
import SplitMergeWrapper from "@/app2components/base/SplitMergeWrapper";
import DropShadowProperties from "@/app2components/panel/element/DropShadowProperties";
import CornerStyleProperties from "@/components/CornerStyleProperties";
import AppConstants from "@/constants/AppConstants";
import { elementSyncUtils } from "@/utils/SyncUtils";
import { gatherProps } from "@/utils/Merge/MergeUtils.mjs";
import {
  BORDER_RADIUS,
  OPACITY,  
  BLUR,
  SHADOW,
  SHADOW_STYLE,
} from "@/constants/MergeConstants";
import { initFormatter } from "@/utils/PropertyFormatters";
import { prepareInputUtils } from "@/utils/InputUtils";
import {
  getEffectsForElementType,
  DROP_SHADOW_ELEMENTS,
  BORDER_RADIUS_ELEMENTS,
  DEFAULT_EFFECTS_VALUES,
} from "@/utils/Element/EffectsPropertyMaps";
import { BASE_INPUT_CONFIG } from "@/utils/Element/BaseInputConfigMap";
import {
  BlurInput,
  OpacityInput,
} from "@/utils/Element/EffectsInputs";
import { useAccountState } from "@/hooks/query/useAccount";

const { ELEMENT_TYPE_SIMPLE, ELEMENT_TYPE_GRAPHIC, CORNER_STYLE_OPTION_NONE } = AppConstants;

const INITIAL_BLUR_VALUE = 10;

/**
 * Reusable effects customize section for Style panels
 * Renders a customize dropdown with effects properties (Opacity, Drop Shadow, Corner Style)
 * Uses the original input components from the Effects panel
 *
 * @param {Object} props
 * @param {import("immutable").Map} props.mappedElement - Mapped element with effects configurations
 * @param {string} props.layout - Current layout (desktop/mobile)
 * @param {Function} props.onPropUpdate - Property update handler
 * @param {import("immutable").Map} props.preparedElement - Prepared element data (conjoined for multi-selection)
 * @param {React.ReactNode} props.children - Additional customized content to render before the menu
 * @param {Array<{label: string, onClick: Function}>} props.extraMenuItems - Additional items to add to the Customize menu
 * @returns {React.ReactElement | null}
 */
const CustomizeEffects = ({ mappedElement, layout, onPropUpdate, preparedElement, children, extraMenuItems = [] }) => {
  const [newCustomProps, setNewCustomProps] = useState([]);
  const [snapshotCustomizedEffects, setSnapshotCustomizedEffects] = useState({
    elementId: null,
    effects: [],
  });
  const user = useAccountState();
  const containerRef = useRef(null);

  const currentElementId = mappedElement?.get("id");
  const elementChanged = currentElementId !== snapshotCustomizedEffects.elementId;

  const pendingSnapshotRef = useRef(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: element.id is intentionally tracked to reset state on element change
  useEffect(() => {
    setNewCustomProps([]);

    if (pendingSnapshotRef.current) {
      setSnapshotCustomizedEffects(pendingSnapshotRef.current);
      pendingSnapshotRef.current = null;
    }
  }, [currentElementId]);

  if (!mappedElement || !layout) {
    return null;
  }

  const element = mappedElement.get("data");
  const elementType = element.get("type", true);
  const isLocked = mappedElement.get("isLocked");

  const allMappedElements = mappedElement.get("_allMappedElements");
  const isMultiSelection = !!allMappedElements;

  const applicableEffects = getEffectsForElementType(elementType);

  const supportsDropShadow = DROP_SHADOW_ELEMENTS.includes(elementType);
  const supportsBorderRadius = BORDER_RADIUS_ELEMENTS.includes(elementType);
  const supportsInsetShadow = [ELEMENT_TYPE_SIMPLE, ELEMENT_TYPE_GRAPHIC].includes(elementType);

  const elementForProps = preparedElement || element;

  const { isSynced, toggleSyncedProp, getPropName } = elementSyncUtils(
    element,
    onPropUpdate,
    layout,
  );

  const { onChange } = initFormatter(onPropUpdate, getPropName);

  const elemProps = gatherProps(
    elementForProps,
    layout,
    ["w", "h", OPACITY, BLUR, BORDER_RADIUS, SHADOW_STYLE],
    true,
  );

  const { canMerge, prepareValue, isSyncMixed } = prepareInputUtils(
    layout,
    elementForProps,
    elemProps,
  );

  const opacity = prepareValue(OPACITY, 100);
  const blur = prepareValue(BLUR, 0);
  const borderRadius = prepareValue(BORDER_RADIUS, CORNER_STYLE_OPTION_NONE);
  const height = elemProps.get("h");
  const width = elemProps.get("w");
  const shadowProps = prepareValue(SHADOW);

  const createToggleSync = (propName, value) => () => toggleSyncedProp(propName, "", value);

  const effectValues = {
    [OPACITY]: opacity,
    [BLUR]: blur,
    [BORDER_RADIUS]: borderRadius,
    [SHADOW_STYLE]: shadowProps?.get?.("style") ?? DEFAULT_EFFECTS_VALUES[SHADOW_STYLE],
  };

  const getToggleSync = (propName) => createToggleSync(propName, effectValues[propName]);

  /**
   * Calculate which effects are currently customized for the element.
   * Reads pre-computed customizedEffects from the mapper (computed in BasePropertyMapper).
   * For multi-selection, returns union of customized effects from all selected elements.
   * @returns {Array<string>} Array of customized effect property names
   */
  const calculateCustomizedEffects = () => {
    const getCustomizedEffects = (mapped) => {
      const effects = mapped.get("customizedEffects");
      return effects?.toJS?.() || effects || [];
    };

    if (isMultiSelection) {
      const allCustomized = new Set();
      allMappedElements.forEach((mapped) => {
        for (const effect of getCustomizedEffects(mapped)) {
          allCustomized.add(effect);
        }
      });
      return Array.from(allCustomized);
    }

    return getCustomizedEffects(mappedElement);
  };

  let effectiveSnapshot;
  if (elementChanged) {
    effectiveSnapshot = { elementId: currentElementId, effects: calculateCustomizedEffects() };
    pendingSnapshotRef.current = effectiveSnapshot;
  } else {
    effectiveSnapshot = snapshotCustomizedEffects;
  }

  /**
   * Check if an effect should be visible in the panel.
   * Uses the snapshot (calculated on element mount) plus any newly added effects.
   * @param {string} propName - The property name constant
   * @returns {boolean} True if effect should be visible
   */
  const isEffectVisible = (propName) => {
    return effectiveSnapshot.effects.includes(propName) || newCustomProps.includes(propName);
  };

  const customizedEffects = [];
  const uncustomizedEffects = [];

  applicableEffects.forEach((effect) => {
    if (effect === SHADOW_STYLE && !supportsDropShadow) return;
    if (effect === BORDER_RADIUS && !supportsBorderRadius) return;

    const label = BASE_INPUT_CONFIG[effect]?.label;

    if (isEffectVisible(effect)) {
      customizedEffects.push(effect);
    } else if (label) {
      uncustomizedEffects.push({ name: effect, label });
    }
  });

  const _onAddCustomization = (prop) => setNewCustomProps([...newCustomProps, prop]);
  const _onRemoveCustomization = (prop) =>
    setNewCustomProps(newCustomProps.filter((propName) => propName !== prop));

  const handleAddCustomization = (propName) => {
    _onAddCustomization(propName);

    if (!isSynced(propName)) {
      const defaultValue = propName === BLUR ? INITIAL_BLUR_VALUE : DEFAULT_EFFECTS_VALUES[propName];
      toggleSyncedProp(propName, "", defaultValue);
    } else if (propName === BLUR) {
      // Blur is already synced so toggleSyncedProp won't write a value.
      // Other effects skip this — their defaults are already "off" states.
      onPropUpdate([{ name: getPropName(propName), value: INITIAL_BLUR_VALUE }]);
    }

    requestAnimationFrame(() => {
      if (containerRef.current) {
        const event = new CustomEvent("si-scroll-to-bottom", {
          bubbles: true,
          composed: true,
        });
        containerRef.current.dispatchEvent(event);
      }
    });
  };

  const handleRemoveCustomization = (propName) => {
    _onRemoveCustomization(propName);
    setSnapshotCustomizedEffects((prev) => ({
      ...prev,
      effects: prev.effects.filter((effect) => effect !== propName),
    }));

    const propToDelete = propName === SHADOW_STYLE ? SHADOW : propName;
    const propIsSynced = isSynced(propName);

    if (!propIsSynced) {
      toggleSyncedProp(propName);
    }

    onPropUpdate([
      {
        action: AppConstants.UPDATE_ACTION_DELETE,
        name: `desktop.${propToDelete}`,
      },
      {
        action: AppConstants.UPDATE_ACTION_DELETE,
        name: `mobile.${propToDelete}`,
      },
    ]);
  };

  return (
    <div ref={containerRef}>
      {customizedEffects.length > 0 && <Separator />}

      {customizedEffects.includes(OPACITY) && (
        <SplitMergeWrapper
          layout={layout}
          mixed={isSyncMixed(OPACITY)}
          sync={isSynced(OPACITY)}
          toggleSync={getToggleSync(OPACITY)}
          removeCustomization={() => handleRemoveCustomization(OPACITY)}
        >
          <OpacityInput
            context={canMerge(OPACITY)}
            disabled={isLocked}
            onBeforeChange={onChange(OPACITY, opacity)}
            onInput={onChange(OPACITY, opacity, false)}
            value={opacity}
          />
        </SplitMergeWrapper>
      )}

      {customizedEffects.includes(BLUR) && (
        <SplitMergeWrapper
          layout={layout}
          mixed={isSyncMixed(BLUR)}
          sync={isSynced(BLUR)}
          toggleSync={getToggleSync(BLUR)}
          removeCustomization={() => handleRemoveCustomization(BLUR)}
        >
          <BlurInput
            context={canMerge(BLUR)}
            disabled={isLocked}
            onBeforeChange={onChange(BLUR, blur)}
            onInput={onChange(BLUR, blur, false)}
            value={blur}
          />
        </SplitMergeWrapper>
      )}

      {customizedEffects.includes(SHADOW_STYLE) && supportsDropShadow && (
        <DropShadowProperties
          disableInsetShadow={!supportsInsetShadow}
          getPropName={getPropName}
          isLocked={isLocked}
          layout={layout}
          mixedSync={isSyncMixed(SHADOW_STYLE)}
          onPropUpdate={onPropUpdate}
          shadowProps={shadowProps}
          sync={isSynced(SHADOW_STYLE)}
          toggleSync={getToggleSync(SHADOW_STYLE)}
          removeCustomization={() => handleRemoveCustomization(SHADOW_STYLE)}
        />
      )}

      {customizedEffects.includes(BORDER_RADIUS) && supportsBorderRadius && (
        <CornerStyleProperties
          isLocked={isLocked}
          layout={layout}
          length={height < width ? height : width}
          mixedSync={isSyncMixed(BORDER_RADIUS)}
          onPropUpdate={onPropUpdate}
          propName={getPropName(BORDER_RADIUS)}
          sync={isSynced(BORDER_RADIUS)}
          toggleSync={getToggleSync(BORDER_RADIUS)}
          usePresets
          value={borderRadius}
          removeCustomization={() => handleRemoveCustomization(BORDER_RADIUS)}
        />
      )}

      {children}

      <Box d="flex" justify="center" flex="col" align="center" mb="3" mt="3" pr="1">
        {(uncustomizedEffects.length > 0 || extraMenuItems.length > 0) && (
          <Menu shift="left" py="lg">
            <Button
              iconPrefix="add"
              iconSize="20"
              variant="minimal"
              slot="trigger-element"
              popovertarget="menu"
              disabled={isLocked}
              data-testid="customize-effects-button"
            >
              Customize
            </Button>
            {uncustomizedEffects.map(({ name, label }) => (
              <MenuItem
                key={name}
                data-testid={`customize-${label.toLowerCase().replace(/\s+/g, "-")}-option`}
                onClick={() => handleAddCustomization(name)}
              >
                {label}
              </MenuItem>
            ))}
            {extraMenuItems.map(({ label, onClick }) => (
              <MenuItem key={label} onClick={onClick}>
                {label}
              </MenuItem>
            ))}
          </Menu>
        )}
      </Box>
    </div>
  );
};

export default CustomizeEffects;
